import {
  GetMeinOrtPostsSortByEnum,
  GetMeinOrtPostsTypesEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';

import { useGetMeinOrtPosts } from '@/features/api/hooks/grapevine-post/useGetMeinOrtPosts';
import {
  MeinOrtChannel,
  MeinOrtTab
} from '@/features/mein-ort/components/meinOrtTypes';
import { PostCard } from '@/features/posts/components/feed/PostCard';
import { PostCardActions } from '@/features/posts/components/feed/PostCardActions';
import { PostCardBody } from '@/features/posts/components/feed/PostCardBody';
import { PostProvider } from '@/features/posts/components/feed/PostCardContext';
import { PostCardHeader } from '@/features/posts/components/feed/PostCardHeader';
import { PostCardImage } from '@/features/posts/components/feed/PostCardImage';
import { PostHeaderInfo } from '@/features/posts/components/feed/PostHeaderInfo';
import { LAST_14_DAYS } from '@/features/posts/constants/params';
import { SectionContent } from '@/features/start/components/components/SectionContent';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { useRefetchOnFocus } from '@/shared/hooks/useRefetchOnFocus';

const styles = StyleSheet.create({
  loading: { paddingVertical: 24, alignItems: 'center' }
});

export const MeinOrtPostsSection: FC = memo(() => {
  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'screens.start.sections.posts'
  });

  const postQuery = useGetMeinOrtPosts({
    types: new Set([GetMeinOrtPostsTypesEnum.NewsItem]),
    count: 1,
    startTime: LAST_14_DAYS,
    sortBy: GetMeinOrtPostsSortByEnum.LastActivity
  });
  const { data: posts, isLoading } = postQuery;
  useRefetchOnFocus(postQuery);

  if (isLoading) {
    return (
      <SectionContent title={t('meinOrt')} testId="section-mein-ort-posts">
        <View style={styles.loading}>
          <ActivityIndicator size="large" />
        </View>
      </SectionContent>
    );
  }

  const post = posts?.[0];

  if (!post) {
    return;
  }

  return (
    <SectionContent
      title={t('meinOrt')}
      testId="section-mein-ort-posts"
      screen={AppRoute.Tabs}
      params={{
        screen: TabRoute.Radio,
        params: { channel: MeinOrtChannel.FUNKZENTRALE, tab: MeinOrtTab.NEWS }
      }}
    >
      <PostProvider value={post}>
        <PostCard>
          <PostCard.Content>
            <PostHeaderInfo />
            <PostCardImage />
            <PostCardHeader />
            <PostCardBody />
          </PostCard.Content>
          <PostCard.Actions>
            <PostCardActions />
          </PostCard.Actions>
        </PostCard>
      </PostProvider>
    </SectionContent>
  );
});

MeinOrtPostsSection.displayName = 'MeinOrtPostsSection';
